import React, { useMemo } from 'react';
import {
  Alert, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Table, Tag, Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ScenarioEconomyCommand } from '../../api/seasonsAdminApi';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Авторский пульт экономики прохождения: блок `economy` и лог команд
 * (RFC_SCENARIO_AUTHORING_ARCHITECTURE §4, §9).
 *
 * Экономика наложена НА СИД для одного прогона и никуда не пишется — это
 * позволяет назначить цену, поставить укрытие на полку и увидеть, окупается ли
 * решение, ДО того как что-то будет опубликовано. Публикация остаётся
 * единственным путём записи в каталог.
 *
 * Команды живут отдельным логом от ухода. Это не украшение контракта: у них
 * разные правила (уход применяется к миру, команда сначала проверяет остаток и
 * только потом меняет и деньги, и мир) и разный разбор отказа.
 */

const COMMAND_TYPES = [
  { value: 'sell_harvest', label: 'Продать урожай' },
  { value: 'buy_item', label: 'Купить предмет' },
  { value: 'install_covering', label: 'Установить укрытие' },
] as const;

const addDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export interface CommandDraft {
  key: string;
  day: number;
  type: ScenarioEconomyCommand['type'];
  crop?: string;
  quantityKg?: number;
  itemId?: string;
  quantity?: number;
  bedId?: string;
  fromDay?: number;
  toDay?: number;
}

/**
 * Черновик → команда ядра. Дни переводятся в даты по startDate шаблона.
 *
 * `sequence` — позиция в логе, а не украшение: несколько команд за один день
 * решаются в этом порядке, и «продать, потом купить на вырученное» отличается от
 * обратного тем, что во втором случае денег не хватит.
 */
export const toEconomyCommand = (
  draft: CommandDraft,
  startDate: string,
  sequence: number,
): ScenarioEconomyCommand => {
  const date = addDays(startDate, draft.day);
  const payload: Record<string, unknown> = {};
  if (draft.type === 'sell_harvest') {
    payload.crop = draft.crop;
    // Пустое количество = продать всё, что лежит на складе (обычное нажатие).
    if (draft.quantityKg != null) payload.quantityKg = draft.quantityKg;
  }
  if (draft.type === 'buy_item') {
    payload.itemId = draft.itemId;
    if (draft.quantity != null) payload.quantity = draft.quantity;
  }
  if (draft.type === 'install_covering') {
    payload.itemId = draft.itemId;
    payload.bedId = draft.bedId;
    payload.activeFrom = addDays(startDate, draft.fromDay ?? draft.day);
    if (draft.toDay != null) payload.activeTo = addDays(startDate, draft.toDay);
  }
  return { id: draft.key, type: draft.type, date, payload, sequence };
};

const exampleEconomy = (crops: string[], bedId: string | undefined) => JSON.stringify({
  currency: 'coin',
  startingBalance: 120,
  rounding: 'round',
  prices: { sell: Object.fromEntries(crops.map((crop) => [crop, 90])) },
  catalog: [{
    id: 'row_cover',
    kind: 'covering',
    title: 'Плёночное укрытие',
    price: 300,
    stock: 1,
    maxWindowDays: 21,
    covering: {
      type: 'parnik',
      parTransmission: 0.82,
      ppfdCalibrationFactor: 1,
    },
  }],
  ...(bedId ? {} : {}),
}, null, 2);

export const ScenarioEconomyComposer: React.FC<{
  startDate: string;
  durationDays: number;
  crops: string[];
  beds: Array<{ value: string; label: string }>;
  /** Ключи предметов из разобранного блока economy — подсказка для команд. */
  itemIds: string[];
  economyJson: string;
  onEconomyJsonChange: (value: string) => void;
  commands: CommandDraft[];
  onCommandsChange: (commands: CommandDraft[]) => void;
}> = ({
  startDate, durationDays, crops, beds, itemIds,
  economyJson, onEconomyJsonChange, commands, onCommandsChange,
}) => {
  const [form] = Form.useForm();
  const type = Form.useWatch('type', form) as ScenarioEconomyCommand['type'] | undefined;

  const jsonError = useMemo(() => {
    if (!economyJson.trim()) return null;
    try {
      const parsed = JSON.parse(economyJson);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return 'economy должен быть объектом';
      }
      return null;
    } catch (err) {
      return (err as Error).message;
    }
  }, [economyJson]);

  const onAdd = async () => {
    const values = await form.validateFields();
    const key = (crypto as { randomUUID?: () => string }).randomUUID?.() || `cmd-${Date.now()}-${Math.random()}`;
    onCommandsChange([...commands, { key, ...values }].sort((a, b) => a.day - b.day));
  };

  return (
    <Card size="small" title="Экономика прохождения (черновик)">
      <Paragraph type="secondary" style={{ marginBottom: 8 }}>
        Блок накладывается на сид только для прогона. В каталог он не попадает —
        запись туда по-прежнему делает публикация.
      </Paragraph>
      <Space style={{ marginBottom: 8 }} wrap>
        <Button
          size="small"
          onClick={() => onEconomyJsonChange(exampleEconomy(crops, beds[0]?.value))}
        >
          Вставить образец
        </Button>
        <Button size="small" onClick={() => onEconomyJsonChange('')} disabled={!economyJson}>
          Убрать экономику
        </Button>
        {itemIds.length > 0 && <Text type="secondary">предметы: {itemIds.join(', ')}</Text>}
      </Space>
      <TextArea
        value={economyJson}
        onChange={(event) => onEconomyJsonChange(event.target.value)}
        placeholder="{ } — без блока сценарий играется на запасах, без денег"
        autoSize={{ minRows: 3, maxRows: 14 }}
        style={{ fontFamily: 'monospace', fontSize: 12 }}
      />
      {jsonError && (
        <Alert type="error" showIcon style={{ marginTop: 8 }} message="JSON не разобран" description={jsonError} />
      )}

      <Form form={form} layout="inline" style={{ marginTop: 16, rowGap: 8 }} initialValues={{ day: 0, type: 'sell_harvest' }}>
        <Form.Item name="day" label="День" rules={[{ required: true, message: 'день' }]}>
          <InputNumber min={0} max={durationDays} style={{ width: 90 }} />
        </Form.Item>
        <Form.Item name="type" label="Команда" rules={[{ required: true }]}>
          <Select options={COMMAND_TYPES as unknown as Array<{ value: string; label: string }>} style={{ width: 190 }} />
        </Form.Item>

        {type === 'sell_harvest' && (
          <>
            <Form.Item name="crop" label="Культура" rules={[{ required: true, message: 'культура' }]}>
              <Select style={{ width: 150 }} options={crops.map((crop) => ({ value: crop, label: crop }))} />
            </Form.Item>
            <Form.Item name="quantityKg" label="кг" tooltip="Пусто = продать всё со склада">
              <InputNumber min={0} step={0.1} style={{ width: 100 }} />
            </Form.Item>
          </>
        )}

        {(type === 'buy_item' || type === 'install_covering') && (
          <Form.Item name="itemId" label="Предмет" rules={[{ required: true, message: 'предмет' }]}>
            <Select
              style={{ width: 180 }}
              options={itemIds.map((id) => ({ value: id, label: id }))}
              placeholder={itemIds.length ? undefined : 'задайте catalog'}
            />
          </Form.Item>
        )}
        {type === 'buy_item' && (
          <Form.Item name="quantity" label="шт">
            <InputNumber min={1} style={{ width: 80 }} />
          </Form.Item>
        )}
        {type === 'install_covering' && (
          <>
            <Form.Item name="bedId" label="Грядка" rules={[{ required: true, message: 'грядка' }]}>
              <Select style={{ width: 170 }} options={beds} />
            </Form.Item>
            <Form.Item name="fromDay" label="с дня" tooltip="Не раньше дня команды: прошлое не переигрывается">
              <InputNumber min={0} max={durationDays} style={{ width: 90 }} />
            </Form.Item>
            <Form.Item name="toDay" label="по день">
              <InputNumber min={0} max={durationDays} style={{ width: 90 }} />
            </Form.Item>
          </>
        )}

        <Form.Item>
          <Button onClick={onAdd}>Добавить команду</Button>
        </Form.Item>
      </Form>

      <Row style={{ marginTop: 12 }}>
        <Col span={24}>
          <Table<CommandDraft>
            rowKey="key"
            size="small"
            pagination={false}
            locale={{ emptyText: 'Команд нет' }}
            dataSource={commands}
            columns={[
              {
                title: 'День',
                dataIndex: 'day',
                width: 130,
                render: (day: number) => (
                  <Space size={4}>
                    <Tag>{day}</Tag>
                    <Text type="secondary">{addDays(startDate, day)}</Text>
                  </Space>
                ),
              },
              {
                title: 'Команда',
                key: 'what',
                render: (_, row) => {
                  if (row.type === 'sell_harvest') {
                    return `продать ${row.crop}${row.quantityKg != null ? ` ${row.quantityKg} кг` : ' (всё)'}`;
                  }
                  if (row.type === 'buy_item') return `купить ${row.itemId} ×${row.quantity ?? 1}`;
                  return `укрыть ${row.bedId} предметом ${row.itemId}: дни ${row.fromDay ?? row.day}–${row.toDay ?? '∞'}`;
                },
              },
              {
                title: '',
                key: 'drop',
                width: 50,
                render: (_, row) => (
                  <Button
                    size="small"
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onCommandsChange(commands.filter((c) => c.key !== row.key))}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default ScenarioEconomyComposer;
